/**
 * The ONE way the admin surfaces write a current-occupancy percentage (OC3).
 *
 * WHY THIS EXISTS. Five admin surfaces printed a per-unit or org-wide figure
 * labelled "Auslastung", and they disagreed with each other in two ways that a
 * reader could see side by side:
 *
 *   - PRECISION. `/admin/rooms` rounded to whole percents (2 of 3 rooms read
 *     "67%"), while the unit detail hero, the occupancy overview and the
 *     Co-Living dashboard printed one decimal ("66.7%"). The same unit could
 *     be two different numbers on two screens.
 *   - ZERO ROOMS. The Co-Living unit row printed "—", the others printed
 *     "0.0%". One says "there is nothing to measure", the other says "nothing
 *     is let" — and a unit whose rooms have never been recorded is the first,
 *     not the second. `rooms/CoLivingUnitRow.jsx` wrote the collision down
 *     rather than resolving it, because resolving it was a product decision.
 *
 * THE DECISION, now settled:
 *
 *   total > 0   ->  "X.X%", exactly one decimal   (0 occupied is "0.0%")
 *   total === 0 ->  "—"                           (no denominator, no rate)
 *   unavailable ->  "—"
 *
 * ZERO ROOMS AND ZERO OCCUPIED ARE NOT THE SAME BRANCH, and that is the whole
 * point. `0 / 4` is a measurement: four rooms, nobody in them, "0.0%". `0 / 0`
 * is the absence of one. The two used to meet in conditions like `if (!rate)`
 * and `total ? … : "0.0%"`, which is how they came to look identical on screen.
 * The guard here is on the DENOMINATOR, never on the rate's truthiness.
 *
 * ZERO ROOMS AND UNAVAILABLE SHARE THE GLYPH, NOT THE STATE. Both print "—"
 * because there is no honest number for either, but they arrive through
 * separate arguments and the callers keep their own distinct loading/error
 * rendering around this string. This helper never decides which state a
 * surface is in; it only writes the number, or declines to.
 *
 * NO SPACE BEFORE THE SIGN. Every existing surface writes "66.7%", and the
 * glyphs are not this slice's to change; precision and the dash are.
 *
 * SCOPE. Current occupancy only. Monthly room-day analytics
 * (`components/admin/companyPerformance`, `companyOverview`), money, margins
 * and map coverage keep their own formatters — this is not a percentage
 * framework, and nothing here knows about population, lease windows or request
 * state. Pure: no clock, no fetch, no mutation.
 */

/** The repo's token for a value it cannot prove. */
export const OCCUPANCY_DASH = "—";

/**
 * EXACTLY ONE DECIMAL, and the one place that decides it.
 *
 * Exported because not every surface writes the percent sign the same way yet:
 * `companyOverview` has always put a non-breaking space before it ("66.7 %") and
 * formats through `Intl` in de-CH, so it cannot take the string below verbatim
 * without a typography change this work is not making. It takes the PRECISION
 * from here instead, which keeps a single answer to "how many decimals" while
 * the glyphs stay the surfaces' own until OC4 settles them.
 */
export const OCCUPANCY_FRACTION_DIGITS = 1;

function isRealNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * @param {object}  args
 * @param {number}  args.total        the denominator — rooms in the population
 * @param {number} [args.occupied]    the numerator, when no server rate exists
 * @param {number} [args.rate]        a rate the backend already computed, in
 *                                    percent; preferred over occupied/total,
 *                                    but NEVER shown when `total` is 0 (the
 *                                    backend sends 0.0 there, and 0.0 is not a
 *                                    rate when nothing can be divided)
 * @param {boolean} [args.unavailable] the surface could not get an answer
 * @returns {string} "X.X%" or "—"
 */
export function occupancyPercentText({ total, occupied, rate, unavailable = false } = {}) {
  if (unavailable) return OCCUPANCY_DASH;
  // THE DENOMINATOR DECIDES, and it is checked before any rate is read.
  if (!isRealNumber(total) || total <= 0) return OCCUPANCY_DASH;

  if (isRealNumber(rate)) return `${rate.toFixed(OCCUPANCY_FRACTION_DIGITS)}%`;
  if (isRealNumber(occupied))
    return `${((occupied / total) * 100).toFixed(OCCUPANCY_FRACTION_DIGITS)}%`;

  // A denominator without a numerator is not a zero; it is an unknown.
  return OCCUPANCY_DASH;
}
